import { Button } from '@design/components/button/Button';
import { Icon } from '@design/components/icon/Icon';
import { Input } from '@design/components/input/Input';
import { authentication } from '@microsoft/teams-js';
import ToggleLogo from '@static/images/brand/logo-dark.svg';
import { teamsPaths } from '@teams/routes/AppRoutes';
import { PasswordInput } from '@terminal/components/forms/password-input/PasswordInput';
import { useEmailValidation } from '@terminal/hooks/use-email-validation/useEmailValidation';
import { useAuth } from '@terminal/stores/use-auth/useAuth';
import { validateCurrentPassword } from '@terminal/utils/password-validation-utils/password-validation-utils';
import { postLogin } from '@terminal/views/auth/services/auth-services';
import { ChangeEvent, FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import styles from './AuthPopup.module.scss';

const defaultState = { email: '', password: '' };

const supportLink = 'https://support.reflexivity.com/hc/en-us/requests/new';

export const AuthPopup = () => {
  const { t } = useTranslation('auth');
  const { initStores } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(defaultState);
  const [errors, setErrors] = useState(defaultState);
  const [apiError, setApiError] = useState('');
  const [processing, setProcessing] = useState(false);

  const updateErrors = (name: string, value: string | undefined) =>
    setErrors(e => ({ ...e, [name]: value ?? '' }));

  const { clearEmailError, validateEmail } = useEmailValidation(error =>
    updateErrors('email', error)
  );

  const clearError = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === 'email') {
      clearEmailError();
    } else {
      updateErrors(e.target.name, '');
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setApiError('');
    setData(d => ({ ...d, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setProcessing(true);

    const passwordError = validateCurrentPassword(data.password);
    updateErrors('password', passwordError);

    const isEmailValid = await validateEmail(data.email, true);

    if (!isEmailValid || passwordError) {
      setProcessing(false);
      return;
    }

    try {
      const response = await postLogin({
        username: data.email,
        password: data.password,
      });

      if (response.error) {
        setApiError(t('auth:login.incorrectCredentialsLabel'));
        return;
      }

      // When this page was opened by authentication.authenticate() the URL
      // carries ?source=teams-popup. In that case we signal the Teams SDK so
      // the main tab's successCallback receives the payload.
      // Otherwise (main tab is showing this page inline because no popup was
      // opened), bootstrap the app directly via initStores — no page reload or
      // cookie dependency needed.
      const isTeamsPopup =
        new URLSearchParams(window.location.search).get('source') ===
        'teams-popup';

      if (isTeamsPopup) {
        authentication.notifySuccess(JSON.stringify(response.data));
      } else {
        await initStores(response.data);
        navigate(teamsPaths.alfred, { replace: true });
      }
    } catch {
      setApiError(t('auth:login.incorrectCredentialsLabel'));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <main
      className={styles.wrapper}
      style={{ backgroundImage: `url('/app/static/images/auth/sun.png')` }}
    >
      <div className={styles.container}>
        <ToggleLogo className={styles.mainLogo} />
        <div className={styles.content}>
          <h1 className={styles.title}>{t('auth:login.loginHeader')}</h1>
          <form className={styles.form} onSubmit={handleSubmit}>
            <Input
              className={styles.styledInput}
              id="email"
              type="email"
              label={t('auth:login.emailText')}
              value={data.email}
              name="email"
              onChange={handleChange}
              onFocus={clearError}
              data-testid="email-input"
              hasError={!!errors.email}
              /* i18next-extract-disable-next-line */
              errorText={t(errors.email)}
            />
            <PasswordInput
              className={styles.styledPasswordInput}
              id="password"
              label={t('auth:login.passwordText')}
              value={data.password}
              name="password"
              onChange={handleChange}
              onFocus={clearError}
              data-testid="password-input"
              hasError={!!errors.password || !!apiError}
              /* i18next-extract-disable-next-line */
              errorText={t(errors.password) || apiError}
            />
            <Button
              fullWidth
              type="submit"
              disabled={processing}
              isLoading={processing}
              label={t('auth:login.loginButtonLabel')}
              data-testid="log-in-btn"
              size="large"
            />
          </form>
          <p className={styles.newUser}>
            {t('auth:login.newUser')}{' '}
            <a
              className={styles.getStartedLink}
              href={supportLink}
              target="_blank"
              rel="noreferrer"
            >
              {t('auth:login.getStarted')}
              <Icon
                iconName="ExternalLink"
                size={16}
                fillColor="currentColor"
                aria-hidden="true"
              />
            </a>
          </p>
        </div>
      </div>
    </main>
  );
};
